import { useEffect, useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Paper, Stack, Switch, TextField, Typography } from '@mui/material';
import { supabase } from '../config/supabase';

type Template = { subject: string; body: string; auto_send_enabled: boolean; version: number };
const defaultSubject = 'Welcome to CourtCaptain — Set Up Your Player Portal';
const defaultBody = `Hi {{player_first_name}},

Welcome to CourtCaptain!

We're using the CourtCaptain Player Portal to make it easier to manage our tennis schedules, match availability, and team information.

Getting Started

1. Visit {{portal_url}}
2. Select Player Portal and log in using the email address associated with your team roster.
3. If this is your first time, use the emailed sign-in link or six-digit code. You may optionally set a password when prompted.

What You Can Do

- Match Availability: Let your captain know which matches you can play.
- Event RSVPs: Respond to practices and team events.
- Playing Preferences: Set your singles, doubles, and scheduling preferences.
- Calendar Invites: Email yourself calendar invitations for matches, practices, and events.
- Team Rosters: View teammates and their shared contact information.
- Contact Sharing: Choose whether to share your email address and/or phone number with teammates.

Add CourtCaptain to Your Phone's Home Screen

You can save the Player Portal to your phone's home screen for quick access, just like an app.

iPhone (Safari)

1. Open the Player Portal in Safari.
2. Tap the Share icon (square with an upward arrow).
3. Select Add to Home Screen, then tap Add.

Android (Chrome)

1. Open the Player Portal in Chrome.
2. Tap the three-dot menu.
3. Select Add to Home screen or Install app, then confirm.

You'll now have a CourtCaptain icon on your home screen for quick access!

Please take a few minutes to log in, set your playing preferences, update your availability, and review your contact-sharing settings.

See you on the courts!

Your CourtCaptain Team
{{team_name}}`;

async function authHeaders() {
  const session = (await supabase.auth.getSession()).data.session;
  return { 'Content-Type': 'application/json', ...(session ? { Authorization: `Bearer ${session.access_token}` } : {}) };
}

export function WelcomeEmailSettings() {
  const [template, setTemplate] = useState<Template | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [testRecipient, setTestRecipient] = useState('');

  useEffect(() => {
    void (async () => {
      const response = await fetch('/api/welcome-email/settings', { headers: await authHeaders() });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) setMessage({ type: 'error', text: result.error || 'Unable to load the welcome email template.' });
      else setTemplate(result.template);
    })();
  }, []);

  const save = async () => {
    if (!template) return;
    setSaving(true);
    const response = await fetch('/api/welcome-email/settings', {
      method: 'PUT', headers: await authHeaders(),
      body: JSON.stringify({ subject: template.subject, body: template.body, autoSendEnabled: template.auto_send_enabled }),
    });
    const result = await response.json().catch(() => ({}));
    setSaving(false);
    if (!response.ok) setMessage({ type: 'error', text: result.error || 'Unable to save the template.' });
    else { setTemplate(result.template); setMessage({ type: 'success', text: 'Player welcome email template saved.' }); }
  };
  const sendTest = async () => {
    if (!template) return;
    setSaving(true);
    const response = await fetch('/api/welcome-email/test', { method: 'POST', headers: await authHeaders(), body: JSON.stringify({ recipient: testRecipient, subject: template.subject, body: template.body }) });
    const result = await response.json().catch(() => ({}));
    setSaving(false);
    setMessage(response.ok ? { type: 'success', text: `Test welcome email sent to ${testRecipient}.` } : { type: 'error', text: result.error || 'Unable to send test email.' });
  };

  if (!template) return message ? <Alert severity={message.type}>{message.text}</Alert> : <Typography>Loading welcome email settings…</Typography>;
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <div><Typography variant="h6" fontWeight={700}>Player Welcome Email</Typography><Typography variant="body2" color="text.secondary">One global template is used when a new player receives their first valid email address. Available variables: {'{{player_first_name}}'}, {'{{player_full_name}}'}, {'{{team_name}}'}, {'{{portal_url}}'}.</Typography></div>
        {message && <Alert severity={message.type}>{message.text}</Alert>}
        <FormControlLabel control={<Switch checked={template.auto_send_enabled} onChange={(event) => setTemplate({ ...template, auto_send_enabled: event.target.checked })} />} label="Automatically send welcome email for eligible players" />
        <TextField label="Subject" value={template.subject} onChange={(event) => setTemplate({ ...template, subject: event.target.value })} fullWidth />
        <TextField label="Message" value={template.body} onChange={(event) => setTemplate({ ...template, body: event.target.value })} fullWidth multiline minRows={12} helperText={`Template version ${template.version}. Plain text is safely converted to email HTML while preserving paragraphs.`} />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button variant="contained" disabled={saving} onClick={() => void save()}>{saving ? 'Saving…' : 'Save Template'}</Button>
          <Button variant="outlined" onClick={() => setPreviewOpen(true)}>Preview</Button>
          <Button variant="outlined" onClick={() => setTemplate({ ...template, subject: defaultSubject, body: defaultBody })}>Restore Default</Button>
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}><TextField size="small" label="Test recipient" type="email" value={testRecipient} onChange={(event) => setTestRecipient(event.target.value)} sx={{ minWidth: 280 }} /><Button variant="outlined" disabled={saving || !testRecipient} onClick={() => void sendTest()}>Send Test Email</Button></Stack>
      </Stack>
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="sm" fullWidth><DialogTitle>{template.subject}</DialogTitle><DialogContent><Typography component="div" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{template.body.replaceAll('{{player_first_name}}', 'Test').replaceAll('{{player_full_name}}', 'Test Player').replaceAll('{{team_name}}', 'Example Team').replaceAll('{{portal_url}}', window.location.origin)}</Typography></DialogContent><DialogActions><Button onClick={() => setPreviewOpen(false)}>Close</Button></DialogActions></Dialog>
    </Paper>
  );
}
